/*
 *  Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
 *
 *  Licensed under the Apache License, Version 2.0 (the "License").
 *  You may not use this file except in compliance with the License.
 *  A copy of the License is located at
 *
 *  http://aws.amazon.com/apache2.0
 *
 *  or in the "license" file accompanying this file. This file is distributed
 *  on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either
 *  express or implied. See the License for the specific language governing
 *  permissions and limitations under the License.
 */
import _ from 'lodash';

// ── Filter name constants ──────────────────────────────────────────
const catalogueFilterNames = {
  ALL: 'all',
  IMPORTED: 'imported',
  APPROVED: 'approved',
  NOT_APPROVED: 'not approved',
  NOT_IMPORTED: 'not imported',
  UPDATE_AVAILABLE: 'update available',
};

// ── Color map (same structure as ScEnvironmentsFilterColors) ───────
const catalogueFilterColorMap = {
  [catalogueFilterNames.ALL]: { bg: '#eff6ff', color: '#2563eb', activeBg: '#2563eb' },
  [catalogueFilterNames.IMPORTED]: { bg: '#f0fdfa', color: '#0d9488', activeBg: '#0d9488' },
  [catalogueFilterNames.UPDATE_AVAILABLE]: { bg: '#fefce8', color: '#ca8a04', activeBg: '#ca8a04' },
  [catalogueFilterNames.APPROVED]: { bg: '#f0fdf4', color: '#16a34a', activeBg: '#16a34a' },
  [catalogueFilterNames.NOT_APPROVED]: { bg: '#fff7ed', color: '#ea580c', activeBg: '#ea580c' },
  [catalogueFilterNames.NOT_IMPORTED]: { bg: '#f3f4f6', color: '#6b7280', activeBg: '#6b7280' },
};

const getCatalogueFilterColor = name =>
  catalogueFilterColorMap[name] || { bg: '#f3f4f6', color: '#6b7280', activeBg: '#6b7280' };

// ── Shared import-status helper ────────────────────────────────────
// Returns one of: 'approved', 'not-approved', 'update-available', 'not-imported'
// This is the same logic previously inlined in CatalogueProductCard.
function getProductImportStatus(product, envTypesStore) {
  if (!envTypesStore) return 'not-imported';
  const productId = product.productId;
  const prefix = `${productId}-`;

  const importedForProduct = _.filter(envTypesStore.list, et => et.id.startsWith(prefix));
  if (importedForProduct.length === 0) return 'not-imported';

  const anyApproved = _.some(importedForProduct, et => et.isApproved);

  // Check for updates using the latest artifact ID from DeploymentStore.
  // This is updated on each deploy, so no SC API call is needed.
  if (product.provisioningArtifactId) {
    const latestId = `${productId}-${product.provisioningArtifactId}`;
    const latestImported = _.some(importedForProduct, et => et.id === latestId);
    if (!latestImported) return 'update-available';
  }

  if (anyApproved) return 'approved';
  return 'not-approved';
}

// ── Which import statuses match each filter? ───────────────────────
// "update-available" products also appear under approved / not-approved
// depending on whether any of their imported versions are approved.
// To support that we need the richer check, so matchesFilter accepts
// the env types store so it can peek at approval when needed.
function productMatchesFilter(filterName, product, envTypesStore) {
  const status = getProductImportStatus(product, envTypesStore);

  switch (filterName) {
    case catalogueFilterNames.ALL:
      return true;
    case catalogueFilterNames.NOT_IMPORTED:
      return status === 'not-imported';
    case catalogueFilterNames.UPDATE_AVAILABLE:
      return status === 'update-available';
    case catalogueFilterNames.IMPORTED:
      return status !== 'not-imported';
    case catalogueFilterNames.APPROVED: {
      if (status === 'approved') return true;
      // update-available products with an approved version count here too
      if (status === 'update-available') {
        const prefix = `${product.productId}-`;
        return _.some(envTypesStore.list, et => et.id.startsWith(prefix) && et.isApproved);
      }
      return false;
    }
    case catalogueFilterNames.NOT_APPROVED: {
      if (status === 'not-approved') return true;
      // update-available products with NO approved version count here
      if (status === 'update-available') {
        const prefix = `${product.productId}-`;
        return !_.some(envTypesStore.list, et => et.id.startsWith(prefix) && et.isApproved);
      }
      return false;
    }
    default:
      return true;
  }
}

export {
  catalogueFilterNames,
  catalogueFilterColorMap,
  getCatalogueFilterColor,
  getProductImportStatus,
  productMatchesFilter,
};
